<template>
  <AppLayout 
    page-title="Bank Accounts" 
    page-subtitle="Connect your bank accounts and manage check deposits"
  >
    <!-- Page Header -->
    <div class="mb-8 pt-8">
      <div class="inline-flex items-center px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-sm font-medium mb-6">
        <span class="w-2 h-2 bg-emerald-400 rounded-full mr-2 animate-pulse"></span>
        Banking & Payments
      </div>
      <h1 class="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">Bank Accounts</h1>
      <p class="text-xl text-slate-300 mb-8 max-w-2xl">Connect multiple bank accounts for faster check deposits and payment processing</p>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <div class="text-center">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-400 mx-auto mb-4"></div>
        <p class="text-slate-400">Loading bank accounts...</p>
      </div>
    </div>

    <!-- Bank Overview -->
    <div v-else class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
      <div class="bank-stat-card">
        <div class="flex items-center justify-center w-12 h-12 bg-emerald-500/20 rounded-xl mb-4">
          <svg class="w-6 h-6 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4z" clip-rule="evenodd"/>
          </svg>
        </div>
        <div>
          <p class="text-sm font-medium text-slate-400 mb-1">Connected Accounts</p>
          <p class="text-3xl font-bold text-white">{{ bankStats.connectedAccounts }}</p>
          <p class="text-sm text-emerald-400">active connections</p>
        </div>
      </div>

      <div class="bank-stat-card">
        <div class="flex items-center justify-center w-12 h-12 bg-blue-500/20 rounded-xl mb-4">
          <svg class="w-6 h-6 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
          </svg>
        </div>
        <div>
          <p class="text-sm font-medium text-slate-400 mb-1">Total Balance</p>
          <p class="text-3xl font-bold text-white">${{ bankStats.totalBalance.toLocaleString() }}</p>
          <p class="text-sm text-blue-400">across all accounts</p>
        </div>
      </div>

      <div class="bank-stat-card">
        <div class="flex items-center justify-center w-12 h-12 bg-amber-500/20 rounded-xl mb-4">
          <svg class="w-6 h-6 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" clip-rule="evenodd"/>
          </svg>
        </div>
        <div>
          <p class="text-sm font-medium text-slate-400 mb-1">Pending Deposits</p>
          <p class="text-3xl font-bold text-white">${{ bankStats.pendingDeposits.toLocaleString() }}</p>
          <p class="text-sm text-amber-400">{{ bankStats.pendingCount }} checks</p>
        </div>
      </div>

      <div class="bank-stat-card">
        <div class="flex items-center justify-center w-12 h-12 bg-purple-500/20 rounded-xl mb-4">
          <svg class="w-6 h-6 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clip-rule="evenodd"/>
          </svg>
        </div>
        <div>
          <p class="text-sm font-medium text-slate-400 mb-1">This Month</p>
          <p class="text-3xl font-bold text-white">${{ bankStats.monthlyDeposits.toLocaleString() }}</p>
          <p class="text-sm text-purple-400">deposited</p>
        </div>
      </div>
    </div>

    <!-- Main Content Grid -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <!-- Connected Bank Accounts -->
      <div class="lg:col-span-2">
        <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 mb-6">
          <div class="flex items-center justify-between mb-6">
            <h2 class="text-2xl font-bold text-white">Connected Bank Accounts</h2>
            <button @click="showAddAccountModal = true" class="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-lg transition-colors">
              Add Account
            </button>
          </div>

          <div class="space-y-4">
            <div v-for="account in bankAccounts" :key="account.id" class="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg hover:bg-slate-600/50 transition-colors">
              <div class="flex items-center space-x-4">
                <div class="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
                  <svg class="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4z" clip-rule="evenodd"/>
                  </svg>
                </div>
                <div>
                  <p class="text-white font-medium">{{ account.bankName }}</p>
                  <p class="text-sm text-slate-400">****{{ account.lastFourDigits }}</p>
                  <p class="text-xs text-slate-500">{{ account.accountType }} • {{ account.status }}</p>
                </div>
              </div>
              <div class="text-right">
                <p class="text-white font-bold">${{ account.balance.toLocaleString() }}</p>
                <p class="text-sm text-slate-400">{{ account.accountType }}</p>
                <div class="flex items-center space-x-2 mt-2">
                  <button @click="editAccount(account)" class="px-3 py-1 bg-blue-500/20 text-blue-400 text-sm rounded-lg hover:bg-blue-500/30 transition-colors">
                    Edit
                  </button>
                  <button @click="removeAccount(account.id)" class="px-3 py-1 bg-red-500/20 text-red-400 text-sm rounded-lg hover:bg-red-500/30 transition-colors">
                    Remove
                  </button>
                </div>
              </div>
            </div>

            <div v-if="bankAccounts.length === 0" class="text-center py-12">
              <div class="w-16 h-16 bg-slate-700/50 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg class="w-8 h-8 text-slate-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4z" clip-rule="evenodd"/>
                </svg>
              </div>
              <p class="text-slate-400 mb-4">No bank accounts connected</p>
              <button @click="showAddAccountModal = true" class="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-lg transition-colors">
                Connect Your First Account
              </button>
            </div>
          </div>
        </div>

        <!-- Recent Check Deposits -->
        <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6">
          <div class="flex items-center justify-between mb-6">
            <h2 class="text-2xl font-bold text-white">Recent Check Deposits</h2>
            <button @click="showDepositModal = true" class="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition-colors">
              Deposit Check
            </button>
          </div>

          <div class="space-y-4">
            <div v-for="deposit in recentDeposits" :key="deposit.id" class="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg">
              <div class="flex items-center space-x-4">
                <div class="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
                  <svg class="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                  </svg>
                </div>
                <div>
                  <p class="text-white font-medium">{{ deposit.checkNumber }}</p>
                  <p class="text-sm text-slate-400">{{ deposit.fromCompany }}</p>
                  <p class="text-xs text-slate-500">{{ formatDate(deposit.depositDate) }}</p>
                </div>
              </div>
              <div class="text-right">
                <p class="text-white font-bold">${{ deposit.amount.toLocaleString() }}</p>
                <p class="text-sm text-slate-400">{{ deposit.status }}</p>
                <p class="text-xs text-slate-500">{{ deposit.accountName }}</p>
              </div>
            </div>

            <div v-if="recentDeposits.length === 0" class="text-center py-8">
              <p class="text-slate-400">No recent deposits</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Quick Actions Sidebar -->
      <div class="space-y-6">
        <!-- Quick Actions -->
        <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6">
          <h3 class="text-xl font-bold text-white mb-4">Quick Actions</h3>
          <div class="space-y-4">
            <button @click="showDepositModal = true" class="w-full flex items-center justify-between p-4 bg-slate-700/30 rounded-lg hover:bg-slate-600/50 transition-colors">
              <div class="flex items-center space-x-3">
                <div class="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <svg class="w-5 h-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clip-rule="evenodd"/>
                  </svg>
                </div>
                <div>
                  <p class="text-white font-medium">Deposit Check</p>
                  <p class="text-sm text-slate-400">Fast mobile deposit</p>
                </div>
              </div>
              <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
              </svg>
            </button>

            <button @click="exportBankData" class="w-full flex items-center justify-between p-4 bg-slate-700/30 rounded-lg hover:bg-slate-600/50 transition-colors">
              <div class="flex items-center space-x-3">
                <div class="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                  <svg class="w-5 h-5 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clip-rule="evenodd"/>
                  </svg>
                </div>
                <div>
                  <p class="text-white font-medium">Export Data</p>
                  <p class="text-sm text-slate-400">Download statements</p>
                </div>
              </div>
              <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
              </svg>
            </button>

            <button @click="scheduleTransfer" class="w-full flex items-center justify-between p-4 bg-slate-700/30 rounded-lg hover:bg-slate-600/50 transition-colors">
              <div class="flex items-center space-x-3">
                <div class="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                  <svg class="w-5 h-5 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd"/>
                  </svg>
                </div>
                <div>
                  <p class="text-white font-medium">Schedule Transfer</p>
                  <p class="text-sm text-slate-400">Between accounts</p>
                </div>
              </div>
              <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- Security Status -->
        <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6">
          <h3 class="text-xl font-bold text-white mb-4">Security Status</h3>
          <div class="space-y-4">
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-3">
                <div class="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                  <svg class="w-4 h-4 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                  </svg>
                </div>
                <span class="text-white">2FA Enabled</span>
              </div>
              <span class="text-emerald-400 text-sm">Active</span>
            </div>
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-3">
                <div class="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                  <svg class="w-4 h-4 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                  </svg>
                </div>
                <span class="text-white">Encryption</span>
              </div>
              <span class="text-emerald-400 text-sm">256-bit</span>
            </div>
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-3">
                <div class="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                  <svg class="w-4 h-4 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                  </svg>
                </div>
                <span class="text-white">Last Login</span>
              </div>
              <span class="text-slate-400 text-sm">{{ formatDate(securityStatus.lastLogin) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Bank Account Modal -->
    <div v-if="showAddAccountModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div class="bg-slate-800/95 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-xl font-bold text-white">{{ editingAccount ? 'Edit Bank Account' : 'Add Bank Account' }}</h3>
          <button @click="closeAddAccountModal" class="text-slate-400 hover:text-white">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        
        <form @submit.prevent="saveBankAccount" class="space-y-6">
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Bank Name</label>
            <input v-model="accountForm.bankName" type="text" required class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white">
          </div>
          
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Account Type</label>
            <select v-model="accountForm.accountType" required class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white">
              <option value="checking">Checking</option>
              <option value="savings">Savings</option>
              <option value="business">Business</option>
            </select>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Account Number</label>
            <input v-model="accountForm.accountNumber" type="text" required class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white" placeholder="Enter last 4 digits">
          </div>
          
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Routing Number</label>
            <input v-model="accountForm.routingNumber" type="text" required class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white">
          </div>
          
          <div class="flex space-x-4">
            <button type="button" @click="closeAddAccountModal" class="flex-1 px-4 py-2 bg-slate-600 hover:bg-slate-700 text-white font-medium rounded-lg transition-colors">
              Cancel
            </button>
            <button type="submit" class="flex-1 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-lg transition-colors">
              {{ editingAccount ? 'Update' : 'Add' }} Account
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Deposit Check Modal -->
    <div v-if="showDepositModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div class="bg-slate-800/95 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-xl font-bold text-white">Deposit Check</h3>
          <button @click="showDepositModal = false" class="text-slate-400 hover:text-white">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        
        <form @submit.prevent="depositCheck" class="space-y-6">
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Select Account</label>
            <select v-model="depositForm.accountId" required class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white">
              <option value="">Choose account</option>
              <option v-for="account in bankAccounts" :key="account.id" :value="account.id">
                {{ account.bankName }} - ****{{ account.lastFourDigits }}
              </option>
            </select>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Check Amount</label>
            <input v-model.number="depositForm.amount" type="number" step="0.01" required class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white" placeholder="0.00">
          </div>
          
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Check Number</label>
            <input v-model="depositForm.checkNumber" type="text" required class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white">
          </div>
          
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">From Company</label>
            <input v-model="depositForm.fromCompany" type="text" required class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white">
          </div>
          
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Upload Check Image</label>
            <input type="file" accept="image/*" @change="handleCheckImageUpload" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white">
          </div>
          
          <div class="flex space-x-4">
            <button type="button" @click="showDepositModal = false" class="flex-1 px-4 py-2 bg-slate-600 hover:bg-slate-700 text-white font-medium rounded-lg transition-colors">
              Cancel
            </button>
            <button type="submit" class="flex-1 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition-colors">
              Deposit Check
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Schedule Transfer Modal -->
    <div v-if="showTransferModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div class="bg-slate-800/95 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-xl font-bold text-white">Schedule Transfer</h3>
          <button @click="showTransferModal = false" class="text-slate-400 hover:text-white">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        
        <form @submit.prevent="saveTransfer" class="space-y-6">
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">From Account</label>
            <select v-model="transferForm.fromAccountId" required class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white">
              <option value="">Select source account</option>
              <option v-for="account in bankAccounts" :key="account.id" :value="account.id">
                {{ account.bankName }} - ****{{ account.lastFourDigits }} (${{ account.balance.toLocaleString() }})
              </option>
            </select>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">To Account</label>
            <select v-model="transferForm.toAccountId" required class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white">
              <option value="">Select destination account</option>
              <option v-for="account in bankAccounts" :key="account.id" :value="account.id">
                {{ account.bankName }} - ****{{ account.lastFourDigits }} (${{ account.balance.toLocaleString() }})
              </option>
            </select>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Transfer Amount</label>
            <input v-model.number="transferForm.amount" type="number" step="0.01" required class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white" placeholder="0.00">
          </div>
          
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Scheduled Date</label>
            <input v-model="transferForm.scheduledDate" type="datetime-local" required class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white">
          </div>
          
          <div class="flex items-center space-x-3">
            <input v-model="transferForm.recurring" type="checkbox" id="recurring" class="form-checkbox">
            <label for="recurring" class="text-sm text-slate-300">Make this a recurring transfer</label>
          </div>
          
          <div v-if="transferForm.recurring">
            <label class="block text-sm font-medium text-slate-300 mb-2">Recurring Frequency</label>
            <select v-model="transferForm.recurringFrequency" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white">
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="yearly">Yearly</option>
            </select>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Notes (Optional)</label>
            <textarea v-model="transferForm.notes" rows="3" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white" placeholder="Add any notes about this transfer"></textarea>
          </div>
          
          <div class="flex space-x-4">
            <button type="button" @click="showTransferModal = false" class="flex-1 px-4 py-2 bg-slate-600 hover:bg-slate-700 text-white font-medium rounded-lg transition-colors">
              Cancel
            </button>
            <button type="submit" class="flex-1 px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white font-medium rounded-lg transition-colors">
              Schedule Transfer
            </button>
          </div>
        </form>
      </div>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import AppLayout from '@/components/layout/AppLayout.vue'
import { useUserStore } from '@/stores/userStore'
import { onMounted, ref } from 'vue'

const userStore = useUserStore()

// Types
interface BankAccount {
  id: number
  bankName: string
  accountType: string
  accountNumber: string
  routingNumber: string
  lastFourDigits: string
  balance: number
  status: string
}

interface CheckDeposit {
  id: number
  accountId: number
  accountName: string
  amount: number
  checkNumber: string
  fromCompany: string
  depositDate: string
  status: string
}

interface BankStats {
  connectedAccounts: number
  totalBalance: number
  pendingDeposits: number
  pendingCount: number
  monthlyDeposits: number
}

interface SecurityStatus {
  lastLogin: string
  twoFactorEnabled: boolean
  encryptionLevel: string
}

// Reactive data
const isLoading = ref(true)
const showAddAccountModal = ref(false)
const showDepositModal = ref(false)
const showTransferModal = ref(false)
const editingAccount = ref<BankAccount | null>(null)

const bankStats = ref<BankStats>({
  connectedAccounts: 0,
  totalBalance: 0,
  pendingDeposits: 0,
  pendingCount: 0,
  monthlyDeposits: 0
})

const bankAccounts = ref<BankAccount[]>([])
const recentDeposits = ref<CheckDeposit[]>([])
const securityStatus = ref<SecurityStatus>({
  lastLogin: new Date().toISOString(),
  twoFactorEnabled: true,
  encryptionLevel: '256-bit'
})

// Form data
const accountForm = ref({
  bankName: '',
  accountType: 'checking',
  accountNumber: '',
  routingNumber: ''
})

const depositForm = ref({
  accountId: '',
  amount: 0,
  checkNumber: '',
  fromCompany: '',
  checkImage: null as File | null
})

const transferForm = ref({
  fromAccountId: '',
  toAccountId: '',
  amount: 0,
  scheduledDate: '',
  recurring: false,
  recurringFrequency: 'monthly',
  notes: ''
})

// API Methods
const fetchBankData = async () => {
  try {
    isLoading.value = true
    
    // Fetch bank statistics
    const statsResponse = await fetch('/api/bank/stats', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userStore.authToken}`
      }
    })
    
    if (statsResponse.ok) {
      const statsData = await statsResponse.json()
      if (statsData.success) {
        bankStats.value = {
          connectedAccounts: statsData.connectedAccounts,
          totalBalance: statsData.totalBalance,
          pendingDeposits: statsData.pendingDeposits,
          pendingCount: statsData.pendingCount,
          monthlyDeposits: statsData.monthlyDeposits
        }
      }
    }
    
    // Fetch bank accounts
    const accountsResponse = await fetch('/api/bank/accounts', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userStore.authToken}`
      }
    })
    
    if (accountsResponse.ok) {
      const accountsData = await accountsResponse.json()
      if (accountsData.success) {
        bankAccounts.value = accountsData.accounts.map((account: any) => ({
          id: account.id,
          bankName: account.bank_name,
          accountType: account.account_type,
          accountNumber: account.account_number,
          routingNumber: account.routing_number,
          lastFourDigits: account.last_four_digits,
          balance: parseFloat(account.balance),
          status: account.status
        }))
      }
    }
    
    // Fetch recent deposits
    const depositsResponse = await fetch('/api/bank/deposits', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userStore.authToken}`
      }
    })
    
    if (depositsResponse.ok) {
      const depositsData = await depositsResponse.json()
      if (depositsData.success) {
        recentDeposits.value = depositsData.deposits.map((deposit: any) => ({
          id: deposit.id,
          accountId: deposit.account_id,
          accountName: deposit.account_name,
          amount: parseFloat(deposit.amount),
          checkNumber: deposit.check_number,
          fromCompany: deposit.from_company,
          depositDate: deposit.deposit_date,
          status: deposit.status
        }))
      }
    }
    
  } catch (error) {
    console.error('Error fetching bank data:', error)
    // Load mock data for demo
    loadMockData()
  } finally {
    isLoading.value = false
  }
}

const loadMockData = () => {
  // Only load mock data if API calls fail
  console.log('Loading mock data due to API failure')
}

const saveBankAccount = async () => {
  try {
    const accountData = {
      ...accountForm.value,
      userId: userStore.currentUser?.id
    }
    
    const response = await fetch('/api/bank/accounts', {
      method: editingAccount.value ? 'PUT' : 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userStore.authToken}`
      },
      body: JSON.stringify(accountData)
    })
    
    if (response.ok) {
      await fetchBankData()
      closeAddAccountModal()
      alert(editingAccount.value ? 'Account updated successfully!' : 'Account added successfully!')
    } else {
      alert('Error saving account')
    }
  } catch (error) {
    console.error('Error saving account:', error)
    alert('Error saving account')
  }
}

const depositCheck = async () => {
  try {
    const formData = new FormData()
    formData.append('accountId', depositForm.value.accountId)
    formData.append('amount', depositForm.value.amount.toString())
    formData.append('checkNumber', depositForm.value.checkNumber)
    formData.append('fromCompany', depositForm.value.fromCompany)
    if (depositForm.value.checkImage) {
      formData.append('checkImage', depositForm.value.checkImage)
    }
    
    const response = await fetch('/api/bank/deposits', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`
      },
      body: formData
    })
    
    if (response.ok) {
      await fetchBankData()
      showDepositModal.value = false
      alert('Check deposited successfully!')
    } else {
      alert('Error depositing check')
    }
  } catch (error) {
    console.error('Error depositing check:', error)
    alert('Error depositing check')
  }
}

const editAccount = (account: BankAccount) => {
  editingAccount.value = account
  accountForm.value = {
    bankName: account.bankName,
    accountType: account.accountType,
    accountNumber: account.accountNumber,
    routingNumber: account.routingNumber
  }
  showAddAccountModal.value = true
}

const removeAccount = async (accountId: number) => {
  if (!confirm('Are you sure you want to remove this account?')) {
    return
  }
  
  try {
    const response = await fetch(`/api/bank/accounts/${accountId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userStore.authToken}`
      }
    })
    
    if (response.ok) {
      await fetchBankData()
      alert('Account removed successfully!')
    } else {
      alert('Error removing account')
    }
  } catch (error) {
    console.error('Error removing account:', error)
    alert('Error removing account')
  }
}

const exportBankData = async () => {
  try {
    // Create CSV data from bank information
    const csvData = [
      ['Bank Accounts Summary'],
      [''],
      ['ACCOUNT DETAILS'],
      ['Bank Name', 'Account Type', 'Last 4 Digits', 'Balance', 'Status'],
      ...bankAccounts.value.map(account => [
        account.bankName,
        account.accountType,
        account.lastFourDigits,
        `$${account.balance.toLocaleString()}`,
        account.status
      ]),
      [''],
      ['RECENT DEPOSITS'],
      ['Date', 'Check Number', 'From Company', 'Amount', 'Account', 'Status'],
      ...recentDeposits.value.map(deposit => [
        formatDate(deposit.depositDate),
        deposit.checkNumber,
        deposit.fromCompany,
        `$${deposit.amount.toLocaleString()}`,
        deposit.accountName,
        deposit.status
      ])
    ]

    // Convert to CSV string
    const csvContent = csvData.map(row => 
      row.map(cell => `"${cell}"`).join(',')
    ).join('\n')

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `bank-data-${new Date().getFullYear()}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)

    alert('Bank data exported successfully!')
  } catch (error) {
    console.error('Error exporting bank data:', error)
    alert('Error exporting bank data')
  }
}

const scheduleTransfer = () => {
  if (bankAccounts.value.length < 2) {
    alert('You need at least 2 bank accounts to schedule transfers')
    return
  }
  showTransferModal.value = true
}

const saveTransfer = async () => {
  try {
    const transferData = {
      fromAccountId: transferForm.value.fromAccountId,
      toAccountId: transferForm.value.toAccountId,
      amount: transferForm.value.amount,
      scheduledDate: transferForm.value.scheduledDate,
      recurring: transferForm.value.recurring,
      recurringFrequency: transferForm.value.recurring ? transferForm.value.recurringFrequency : null,
      notes: transferForm.value.notes
    }
    
    const response = await fetch('/api/bank/transfers', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userStore.authToken}`
      },
      body: JSON.stringify(transferData)
    })
    
    if (response.ok) {
      const result = await response.json()
      if (result.success) {
        showTransferModal.value = false
        resetTransferForm()
        alert('Transfer scheduled successfully!')
      } else {
        alert(result.error || 'Error scheduling transfer')
      }
    } else {
      const errorData = await response.json()
      alert(errorData.error || 'Error scheduling transfer')
    }
  } catch (error) {
    console.error('Error scheduling transfer:', error)
    alert('Error scheduling transfer')
  }
}

const resetTransferForm = () => {
  transferForm.value = {
    fromAccountId: '',
    toAccountId: '',
    amount: 0,
    scheduledDate: '',
    recurring: false,
    recurringFrequency: 'monthly',
    notes: ''
  }
}

const handleCheckImageUpload = (event: Event) => {
  const target = event.target as HTMLInputElement
  if (target.files && target.files[0]) {
    depositForm.value.checkImage = target.files[0]
  }
}

const closeAddAccountModal = () => {
  showAddAccountModal.value = false
  editingAccount.value = null
  accountForm.value = {
    bankName: '',
    accountType: 'checking',
    accountNumber: '',
    routingNumber: ''
  }
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

// Load data on mount
onMounted(() => {
  fetchBankData()
})
</script>

<style scoped>
.bank-stat-card {
  @apply bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 hover:border-emerald-500/50 transition-all duration-300;
}
</style>
